<script setup lang="ts">
import { computed, inject } from 'vue';
import type { ComputedRef } from 'vue';
import type { UserRole } from 'src/types/User';
import {
  mdiBookOpenPageVariantOutline,
  mdiFormatListBulletedType,
} from '@quasar/extras/mdi-v7';

// Role-aware empty state for a student's / child's course & section lists: explains
// why the list is empty and what to do. Children self-serve from the store; school
// students receive assignments from their teacher (a request/messaging flow is
// planned — for now the copy tells them to ask their teacher).
interface Props {
  contentType: 'course' | 'section';
}

const props = defineProps<Props>();

const currentUserRole =
  inject<ComputedRef<UserRole | undefined>>('currentUserRole');

const isCourse = computed(() => props.contentType === 'course');

const icon = computed(() =>
  isCourse.value ? mdiBookOpenPageVariantOutline : mdiFormatListBulletedType,
);

const titleKey = computed(() =>
  isCourse.value ? 'CONTENT_EMPTY_COURSE_TITLE' : 'CONTENT_EMPTY_SECTION_TITLE',
);

const messageKey = computed(() => {
  const role = currentUserRole?.value;
  if (role === 'child') {
    return isCourse.value
      ? 'CONTENT_EMPTY_COURSE_CHILD'
      : 'CONTENT_EMPTY_SECTION_CHILD';
  }
  if (role === 'student') {
    return isCourse.value
      ? 'CONTENT_EMPTY_COURSE_STUDENT'
      : 'CONTENT_EMPTY_SECTION_STUDENT';
  }
  // Teacher preview lists fill from the "Preview" button inside their classes —
  // an empty list means "nothing previewed yet", not "nothing assigned to you".
  if (role === 'teacher') {
    return 'CONTENT_EMPTY_TEACHER_PREVIEW';
  }
  return 'CONTENT_EMPTY_GENERIC';
});

// Children self-serve from the course store (sections come with courses; there
// is no section store); teachers preview from their classes; students wait on
// their teacher.
const storeAction = computed<{ routeName: string; labelKey: string } | null>(
  () => {
    if (currentUserRole?.value === 'teacher') {
      return {
        routeName: 'school.teacher.home',
        labelKey: 'GO_TO_MY_CLASSES',
      };
    }
    if (currentUserRole?.value !== 'child') return null;
    return { routeName: 'courses', labelKey: 'GO_TO_COURSE_STORE' };
  },
);
</script>

<template>
  <div class="content-empty flex flex-center q-pa-md">
    <div class="content-empty__inner text-center">
      <div class="content-empty__icon flex flex-center q-mx-auto q-mb-md">
        <q-icon
          :name="icon"
          size="40px"
          color="primary"
        />
      </div>

      <div class="text-h6 text-weight-bold q-mb-sm">
        {{ $t(titleKey) }}
      </div>

      <div class="text-body1 text-muted q-mb-lg">
        {{ $t(messageKey) }}
      </div>

      <q-btn
        v-if="storeAction"
        unelevated
        color="primary"
        :label="$t(storeAction.labelKey)"
        :to="{ name: storeAction.routeName }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.content-empty {
  min-height: 50vh;

  &__inner {
    width: 100%;
    max-width: 26rem;
  }

  &__icon {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: rgba(151, 71, 255, 0.12);
  }
}
</style>
